import { locationsApi } from 'api/locations'
import { useGetPurchaseMatchRateQuery } from 'api/purchaseMatchRate'
import ModalContainer from 'components/ModalContainer'
import { buildTranslate } from 'locales'
import moment from 'moment'
import { useMemo } from 'react'
import type { MonthRange } from 'slices/aiConversations/calloutTypes'
import { useStyletron } from 'styletron-react'
import { useIsMobile } from 'utilities/responsive'
import { buildColumns, buildRows } from './buildTable'
import PurchaseDataTable from './PurchaseDataTable'

const t = buildTranslate('components.ai.purchase_data')

const DESKTOP_WIDTH = '1000px'
const DESKTOP_DIALOG_STYLE = { marginTop: '10vh', maxWidth: '100%' }
const MOBILE_DIALOG_STYLE = {
  height: '100vh',
  maxHeight: 'none',
  margin: 0,
  borderRadius: 0,
}
const TABLE_MAX_HEIGHT = '55vh'

type Props = {
  months: MonthRange | null
  onClose: () => void
}

const PurchaseDataModal = ({ months, onClose }: Props) => {
  const [css] = useStyletron()
  const isMobile = useIsMobile()
  const columns = useMemo(() => buildColumns(moment(), months), [months])
  const purchases = useGetPurchaseMatchRateQuery({
    months: columns.map(({ month }) => month),
  })
  const locations = locationsApi.useGetLocationsQuery({ coming_soon: false })

  const locationNames = useMemo(
    () =>
      new Map(
        (locations.data?.locations ?? []).map(location => [
          location.id,
          location.name || location.street,
        ])
      ),
    [locations.data]
  )

  const rows = useMemo(() => {
    if (!purchases.data?.length) return []
    const locationOrder = Array.from(locationNames.keys()).sort((a, b) =>
      (locationNames.get(a) ?? '').localeCompare(locationNames.get(b) ?? '')
    )
    return buildRows(purchases.data, columns, locationOrder)
  }, [purchases.data, columns, locationNames])

  const retry = () => {
    if (purchases.isError) purchases.refetch()
    if (locations.isError) locations.refetch()
  }

  return (
    <ModalContainer
      isOpen
      onClose={onClose}
      title={t('title')}
      subtitle={t('definition')}
      width={isMobile ? '100%' : DESKTOP_WIDTH}
      position={isMobile ? undefined : 'top'}
      dialogStyle={isMobile ? MOBILE_DIALOG_STYLE : DESKTOP_DIALOG_STYLE}
      hideCancel
      showBottomProceed={false}
    >
      <div
        className={`mt-m ${css({
          maxHeight: TABLE_MAX_HEIGHT,
          overflowY: 'auto',
        })}`}
      >
        <PurchaseDataTable
          columns={columns}
          rows={rows}
          locationNames={locationNames}
          isLoading={purchases.isFetching || locations.isFetching}
          isError={purchases.isError || locations.isError}
          onRetry={retry}
        />
      </div>
    </ModalContainer>
  )
}

export default PurchaseDataModal
